import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Link as RouterLink } from 'react-router-dom';
import { Alert, Box, Button, ButtonBase, Collapse, Skeleton, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import useAllowedAccess from '@tenant/core/AppPermission/useAllowedAccess';
import { AuthorizePermission } from '@tenant/utility/constants/authorizePermission';
import { ROUTE_STRING } from 'constants/RouteString';
import useGetPipelineRunLog from 'redux/slices/pipelines/hooks/useGetPipelineRunLog';
import { resolveRunLogMessage } from 'redux/slices/pipelines/helpers/resolveRunLogMessage';
import { PipelineRunStatus } from 'redux/slices/pipelines/constants/pipelineRunStatus';
import RunErrorText from './RunErrorText';

// deploy-pipelines-run-logs (Wave 3) AC-1/AC-4/AC-5/AC-6/AC-12/AC-13/AC-14/AC-20 — the
// collapsible build-log panel. Rendered by PipelineFlow inside the BUILD stage's
// TimelineContent and nowhere else: the Deploy stage gets no log affordance of any kind
// (AC-17), because deploy-leg logs have no source at any layer and an affordance that can
// never work is worse than none.
//
// AC-14 / THE RENDER EDGE — this is the security-load-bearing part of the file. Build output
// is publisher-controlled untrusted text of exactly the class `developer-portal-log-output-xss`
// was filed against. It is rendered as a React TEXT CHILD inside a `whiteSpace: 'pre-wrap'`
// container, following RunErrorText.js verbatim in shape. There is no `dangerouslySetInnerHTML`
// in this file, no sanitiser, and no transformation of any kind — the log is passed through
// unaltered, because a log viewer that mangles the log it is showing you is a worse tool.
//
// AC-19 — nothing here claims the log is anything other than a periodically re-read snapshot
// of a stored column. No streaming transport exists in this codebase and no wording in this
// file implies one.
//
// Styling is theme-owned only: background.default / action.hover / divider / text.primary, and
// monospace on the log body alone. No hardcoded hex and specifically NOT the mock's near-black
// terminal panel — Wave 1 rejected it, and dark mode plus user-selectable palettes are both
// live in this app (ColorSets.js ships light AND dark sets), so a fixed dark surface would be
// unreadable in half of them.

const LOG_BODY_MAX_HEIGHT = 320;

// The tail's own line count, computed from the text actually on screen rather than from a
// constant, so the number in the sentence can never disagree with the number of lines rendered.
//
// This MIRRORS PipelineRunLogTail.TakeLastLines' counting rule exactly: a single trailing
// terminator does not open a new line, so "a\nb\n" is two lines, not three. A naive
// `split('\n').length` counts the empty string after the final terminator as a line — and since
// most ACR logs DO end with a newline, that made a 200-line tail announce itself as "the last
// 201 lines", one more than MaxLines can ever return. If this rule and the BE's ever diverge
// again, the sentence starts lying about the thing sitting directly beneath it.
const countLines = text => {
	if (!text) {
		return 0;
	}
	const separators = (text.match(/\n/g) || []).length;
	return text.endsWith('\n') ? separators : separators + 1;
};

const BuildLogPanel = ({ pipelineId, run, stageStatus }) => {
	const runId = run?.Id;
	const status = run?.Status;
	const isBuilding = status === PipelineRunStatus.Building;

	// `isLoading` is deliberately not destructured: with a runId always present (PipelineFlow
	// guarantees it) `!log && !isError` IS the first-load window, and deriving the placeholder
	// from the data rather than from a flag means there is no combination of the two that can
	// leave a bordered box with nothing in it (AC-12).
	const { log, isError } = useGetPipelineRunLog({ pipelineId, runId, status });

	// AC-20 — the link-out to the Docker-Images log page is gated on the permission THAT page's
	// API actually requires. `GET /api/docker-repositories/{id}/logs` is REPOSITORY_READ, while
	// this panel deliberately needs only PIPELINE_READ, so an unconditional link would 403
	// precisely the publisher this slice exists to serve. Without the permission there is NO
	// link — not a disabled one, which would only advertise a page they cannot open.
	const canViewRepositoryLogs = useAllowedAccess([AuthorizePermission.REPOSITORY_READ]);

	// The route's `:lastRunId` param is, despite its name, the DockerImageMetaData Id. The
	// shipped log DTO does not carry it, but the run object this page already holds does — and
	// it is genuinely null in production (stamped only at the flip to Building), so a null is a
	// normal case that simply yields no link, never a broken one.
	const dockerImageMetaDataId = run?.DockerImageMetaDataId;

	// AC-6 — default expansion is DERIVED from the stage's state rather than latched at mount:
	// active -> expanded, failed -> expanded, succeeded -> collapsed. (`pending` never gets
	// here; PipelineFlow does not render this component at all in that state.) A deliberate
	// toggle by the reader overrides the default from then on, so a live active -> succeeded
	// transition cannot slam shut a panel someone chose to open.
	//
	// Nothing in this component calls .focus(): auto-expanding on a failed run must not steal
	// focus from wherever the reader is (NFR-Accessibility).
	const [userExpanded, setUserExpanded] = useState(null);
	// M1: `cancelled` auto-expands alongside active/failed. A reader who just stopped a build is
	// looking at that build — expanding is the same reasoning that expands a failed one, and it is
	// the whole point of keeping the panel mounted for a cancelled run.
	const autoExpanded = stageStatus === 'active' || stageStatus === 'failed' || stageStatus === 'cancelled';
	const expanded = userExpanded ?? autoExpanded;

	// `stageStatus === 'cancelled'` rather than reading run.Status: this component is already handed
	// the derived stage state, and the derivation (deriveRunFlowNodeStatus) is the single place that
	// decides what a cancelled run's build stage actually is. Reading the raw status here would give
	// the panel a second, independently-drifting opinion.
	const message = resolveRunLogMessage(log, isBuilding, stageStatus === 'cancelled');
	const logText = message === null ? log?.Logs : null;
	const buildError = log?.BuildError;

	const lineCount = useMemo(() => countLines(logText), [logText]);

	const headerId = `build-log-header-${runId}`;
	const bodyId = `build-log-body-${runId}`;

	return (
		<Box sx={{ mt: 1.5, border: 1, borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
			<Box
				sx={{
					display: 'flex',
					alignItems: 'center',
					gap: 1,
					bgcolor: 'action.hover',
					borderBottom: expanded ? 1 : 0,
					borderColor: 'divider'
				}}
			>
				{/* A real <button> (ButtonBase), so it is keyboard operable and focusable for
				    free. Its accessible name comes from its own text content; `aria-expanded`
				    and `aria-controls` carry the state, which is therefore never conveyed by
				    the chevron's rotation alone. */}
				<ButtonBase
					id={headerId}
					onClick={() => setUserExpanded(!expanded)}
					aria-expanded={expanded}
					aria-controls={bodyId}
					sx={{ flex: 1, minWidth: 0, justifyContent: 'flex-start', gap: 1, px: 1.5, py: 1, textAlign: 'left' }}
				>
					<ExpandMoreIcon
						fontSize="small"
						aria-hidden="true"
						sx={{ color: 'text.secondary', transform: expanded ? 'rotate(180deg)' : 'none', transition: 'transform 150ms' }}
					/>
					<Typography variant="caption" color="text.primary">
						Build log
					</Typography>
				</ButtonBase>

				{canViewRepositoryLogs && dockerImageMetaDataId && (
					<Button
						component={RouterLink}
						to={ROUTE_STRING.DOCKER_IMAGE.LOG.replace(':lastRunId', dockerImageMetaDataId)}
						size="small"
						sx={{ mr: 1, flexShrink: 0 }}
					>
						Open full log
					</Button>
				)}
			</Box>

			<Collapse in={expanded}>
				<Box sx={{ p: 1.5 }}>
					{/* AC-13 — a dropped fetch is an inline, non-blocking notice. It sits ABOVE
					    whatever was last fetched, which stays on screen: the hook never wipes
					    `log` on failure. No page-level error state is entered from here. */}
					{isError && (
						<Alert severity="warning" variant="outlined" sx={{ mb: 1.5 }}>
							{log ? 'Could not refresh the build log. Showing the last version fetched.' : 'Could not load the build log.'}
						</Alert>
					)}

					{!log ? (
						// First load: a bounded placeholder that always resolves — never a
						// spinner with no end state (AC-12). Once the fetch has failed with
						// nothing yet fetched, the notice above IS the whole answer, so no
						// placeholder is left spinning underneath it.
						!isError && <Skeleton variant="rounded" height={96} />
					) : (
						<>
							{/* AC-5 — the ACR run's OWN error. This is a DIFFERENT string from
							    PipelineRun.ErrorMessage, which for a build failure is only the
							    orchestrator's "Build ended with status 'X'." summary rendered in
							    the page's red "Run failed" banner above. Distinguished three
							    ways so the reader can tell which string came from where: it is
							    labelled with its source in words, it sits inside the Build log
							    panel rather than at page level, and it carries a warning-toned
							    left rule instead of the banner's error-toned one. Rendered
							    through RunErrorText, i.e. as text, like everything else here. */}
							{buildError && (
								<Box sx={{ mb: 1.5, p: 1.5, bgcolor: 'action.hover', borderLeft: 3, borderColor: 'warning.main', borderRadius: 1 }}>
									<Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
										Build error reported by the container registry
									</Typography>
									<RunErrorText message={buildError} maxHeight={120} />
								</Box>
							)}

							{message !== null ? (
								<Typography variant="body2" color="text.secondary">
									{message}
								</Typography>
							) : (
								<>
									{/* AC-4 — say it, in words the reader can act on. The tail is
									    never presented as if it were the whole log: this names
									    both what is on screen and how much there is in total. */}
									{log?.Truncated && (
										<Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
											{`Showing only the END of the build output — the last ${lineCount.toLocaleString()} ` +
												`${lineCount === 1 ? 'line' : 'lines'}, ` +
												`${(log.ReturnedLength ?? 0).toLocaleString()} of ${(log.TotalLength ?? 0).toLocaleString()} characters.`}
										</Typography>
									)}

									{/* The log body. A keyboard-reachable (tabIndex 0) scroll
									    region with an accessible name, bounded in height so a
									    long log cannot push the rest of the page away, and
									    bounded in WIDTH by overflowWrap/wordBreak so a 2 000-char
									    unbroken base64 blob wraps in here instead of making the
									    page body scroll sideways (the same pair the XSS fix
									    used; 'break-word' is the fallback for Safari < 15.4,
									    which ignores 'anywhere'). overflowX is pinned hidden so
									    there is no horizontal scrollbar to inherit either. */}
									<Box
										id={bodyId}
										role="region"
										aria-label="Build log output"
										tabIndex={0}
										sx={{
											maxHeight: LOG_BODY_MAX_HEIGHT,
											overflowY: 'auto',
											overflowX: 'hidden',
											p: 1.5,
											bgcolor: 'background.default',
											border: 1,
											borderColor: 'divider',
											borderRadius: 1
										}}
									>
										<Typography
											variant="body2"
											component="span"
											color="text.primary"
											sx={{
												display: 'block',
												fontFamily: 'monospace',
												whiteSpace: 'pre-wrap',
												overflowWrap: 'anywhere',
												wordBreak: 'break-word'
											}}
										>
											{logText}
										</Typography>
									</Box>
								</>
							)}
						</>
					)}
				</Box>
			</Collapse>
		</Box>
	);
};

export default React.memo(BuildLogPanel);

BuildLogPanel.propTypes = {
	pipelineId: PropTypes.string.isRequired,
	run: PropTypes.shape({
		Id: PropTypes.string,
		Status: PropTypes.number,
		DockerImageMetaDataId: PropTypes.string
	}).isRequired,
	// The Build stage's DERIVED status from deriveRunFlowNodeStatus ('active' | 'success' |
	// 'failed'). Never 'pending' — PipelineFlow does not render this component in that state.
	// `cancelled` added by the epic-wide adversarial review (M1). The original list encoded the
	// assumption Wave 5 later broke — its comment read "Never 'pending'" because PipelineFlow does
	// not render this component for an unstarted stage, but Wave 5's Cancelled arm made `pending`
	// the state of a build that HAD run, which both unmounted this panel and tripped this propType
	// in the process. A cancelled build has real output and belongs here.
	stageStatus: PropTypes.oneOf(['active', 'success', 'failed', 'cancelled']).isRequired
};
